import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import { addQueryWithReplace, QueryVal } from 'Helpers/Query';
import useLink from 'Hooks/useLink';

const ListDetailViewContext = React.createContext(undefined);

const selectedResourceQueryParam = 'listDetailViewSelectedResourceId';

const init = () => {
  return {
    selectedResourceId: QueryVal(selectedResourceQueryParam),
    selectedResourceRelationshipLink: null,
    listCollection: null,
    listLoading: false,
    listLoadingError: null,
    isInitializing: true,
  };
};

const SET_SELECTED_RESOURCE_ID = 'SET_SELECTED_RESOURCE_ID';
const SET_SELECTED_RESOURCE_RELATIONSHIP_LINK = 'SET_SELECTED_RESOURCE_RELATIONSHIP_LINK';
const SET_LIST_ITEM_RESOURCE = 'SET_LIST_ITEM_RESOURCE';
const SET_LIST_COLLECTION = 'SET_LIST_COLLECTION';
const SET_LIST_LOADING = 'SET_LIST_LOADING';
const SET_LIST_LOADING_ERROR = 'SET_LIST_LOADING_ERROR';
const SET_LIST_INITIALIZING = 'SET_LIST_INITIALIZING';

const reducer = (state, action) => {
  switch (action.type) {
    case SET_SELECTED_RESOURCE_ID: {
      addQueryWithReplace({ [selectedResourceQueryParam]: action.selectedResourceId });
      return {
        ...state,
        selectedResourceId: action.selectedResourceId,
      };
    }
    case SET_SELECTED_RESOURCE_RELATIONSHIP_LINK: {
      return {
        ...state,
        selectedResourceRelationshipLink: action.selectedResourceRelationshipLink,
      };
    }
    case SET_LIST_ITEM_RESOURCE: {
      const listCollection = state.listCollection.copy();
      listCollection.resources().updateItem(action.listItemResource);
      return {
        ...state,
        listCollection,
      };
    }
    case SET_LIST_COLLECTION: {
      return {
        ...state,
        listCollection: action.listCollection,
      };
    }
    case SET_LIST_LOADING: {
      return {
        ...state,
        listLoading: action.listLoading,
      };
    }
    case SET_LIST_LOADING_ERROR: {
      return {
        ...state,
        listLoadingError: action.listLoadingError,
      };
    }
    case SET_LIST_INITIALIZING: {
      return {
        ...state,
        isInitializing: action.listIsInitializing,
      };
    }
    default: {
      throw new Error(`Unhandled ListDetailViewContext action type: ${action.type}`);
    }
  }
};

function ListDetailViewProvider({ children, listCollectionSelfLink }) {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const {
    isInitializing: listIsInitializing,
    loading: listLoading,
    loadingError: listLoadingError,
    collection: listCollection,
    refreshCollection: refreshListCollection,
  } = useLink(listCollectionSelfLink);

  // Update state list collection whenever the list collection changes
  React.useEffect(() => {
    dispatch({ type: SET_LIST_COLLECTION, listCollection });
  }, [listCollection]);

  // Update state loading whenever the list loading changes
  React.useEffect(() => {
    dispatch({ type: SET_LIST_LOADING, listLoading });
  }, [listLoading]);

  // Update state loading error whenever the list loading error changes
  React.useEffect(() => {
    dispatch({ type: SET_LIST_LOADING_ERROR, listLoadingError });
  }, [listLoadingError]);

  // Update state isInitializing whenever the list isInitializing changes
  React.useEffect(() => {
    dispatch({ type: SET_LIST_INITIALIZING, listIsInitializing });
  }, [listIsInitializing]);

  // Update the relationship link for the detail view whenever the selected resource changes.
  React.useEffect(() => {
    if (!state.listCollection) {
      return;
    }
    const selectedResource = state.listCollection.resources().findItem(el => el.id() === state.selectedResourceId);

    // Default to first list item if we have an invalid selected resource
    if (!selectedResource) {
      const selectedResourceId = state.listCollection?.resources()?.head()?.id();
      dispatch({ type: SET_SELECTED_RESOURCE_ID, selectedResourceId });
      return;
    }

    // Set the detail item relationship link for the selected resource
    const relationshipLink = selectedResource?.relationship(fr.relationships.detailItem.name);

    dispatch({ type: SET_SELECTED_RESOURCE_RELATIONSHIP_LINK, selectedResourceRelationshipLink: relationshipLink });
  }, [state.listCollection, state.selectedResourceId]);

  const setSelectedResourceId = React.useCallback((selectedResourceId) => {
    dispatch({ type: SET_SELECTED_RESOURCE_ID, selectedResourceId });
  }, []);

  const setListItemResource = React.useCallback((listItemResource) => {
    dispatch({ type: SET_LIST_ITEM_RESOURCE, listItemResource });
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      selectedResourceId: state.selectedResourceId,
      selectedResourceRelationshipLink: state.selectedResourceRelationshipLink,
      listCollection: state.listCollection,
      listLoading: state.listLoading,
      listLoadingError: state.listLoadingError,
      listIsInitializing: state.isInitializing,
      setSelectedResourceId,
      setListItemResource,
      refreshListCollection,
    };
  }, [state, refreshListCollection]);

  return (
    <ListDetailViewContext.Provider value={memoizedValue}>
      {children}
    </ListDetailViewContext.Provider>
  );
}

function useListDetailViewContext() {
  const context = React.useContext(ListDetailViewContext);

  if (context === undefined) {
    throw new Error('useListDetailView must be used within a ListDetailViewProvider');
  }

  return context;
}

ListDetailViewProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  listCollectionSelfLink: PropTypes.object.isRequired,
};

export {
  ListDetailViewProvider,
  useListDetailViewContext,
};
